/* eslint-disable @typescript-eslint/no-explicit-any,@typescript-eslint/ban-ts-comment */
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SpreeService } from '../../../services/spree-client/storefront/spree.service';
import { map, Observable } from 'rxjs';
import { Variant } from './product.types';
import {
  Image,
  OptionValue,
  ProductVariant,
  Property,
} from '../product-list/product-list.mapper';
import {
  IProduct,
  JsonApiDocument,
  ProductAttr,
  RelationType,
  TaxonAttr,
} from '../../../services/spree-client/storefront';
import { extractRelationships } from '../../../helpers/serializers';
import { StateService } from '../../../services/state.service';
import { Category } from '../product-list/product-list.types';
import { taxonToCategory } from '../../../serializers/category.serializer';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private activeRoute = inject(ActivatedRoute);
  private spree = inject(SpreeService);
  private state = inject(StateService);

  getProduct(): Observable<Variant[]> {
    return this.spree.products
      .show({
        id: this.activeRoute.snapshot.params['productUrl'],
        fields: {
          product:
            'name,slug,sku,localized_slugs,description,primary_variant,default_variant,variants,option_types,product_properties,total_on_hand,public_metadata,available',
          variant:
            'sku,price,display_price,compare_at_price,in_stock,product,images,option_values,is_master,total_on_hand,public_metadata',
        },
        include:
          'primary_variant,default_variant,variants.option_values,option_types,product_properties,taxons',
        currency: this.state.getActiveCurrency(),
      })
      .pipe(
        map((p) => {
          return deserializeSingleProductVariants(p) as Variant[];
        }),
      );
  }
}

export const deserializeSingleProductVariants = (
  apiProduct: IProduct,
): ProductVariant[] => {
  const attachments = apiProduct.included ?? [];
  const productId = apiProduct.data.id;
  const primaryVariantId = maybePrimaryVariantId(apiProduct.data);

  const groupedVariants = groupIncluded<'primaryVariants' | 'optionVariants'>(
    attachments,
    {
      primaryVariants: (document) =>
        isVariantOfProduct(document, productId) && document.id === primaryVariantId,
      optionVariants: (document) =>
        isVariantOfProduct(document, productId) && document.id !== primaryVariantId,
    },
  );

  if (groupedVariants.optionVariants.length === 0) {
    const images = deserializeImages(apiProduct.data, groupedVariants.primaryVariants);

    return [
      {
        ...partialDeserializeProductVariant(
          apiProduct.data,
          groupedVariants.primaryVariants[0],
          attachments,
        ),
        images,
      },
    ];
  }

  return groupedVariants.optionVariants.map((variant) => {
    const images = deserializeImages(apiProduct.data, [
      ...groupedVariants.primaryVariants,
      variant,
    ]);

    return {
      ...partialDeserializeProductVariant(apiProduct.data, variant, attachments),
      images,
    };
  });
};

const maybePrimaryVariantId = (product: ProductAttr): RelationType['id'] | undefined =>
  // primary_variant may not exist if an older version of Spree is used. Only use primary_variant if available.
  (product.relationships['primary_variant']?.data as RelationType)?.id;

const groupIncluded = <Groups extends keyof never>(
  included: JsonApiDocument[],
  discriminators: { [key: string]: (document: JsonApiDocument) => boolean },
): { [key in Groups]: JsonApiDocument[] } => {
  const discriminatorsKeys = Object.keys(discriminators);

  const emptyGroups = discriminatorsKeys.reduce((accumulatedGroups, discriminatorKey) => {
    // @ts-ignore
    accumulatedGroups[discriminatorKey] = [];

    return accumulatedGroups;
  }, {});

  // @ts-ignore
  return included.reduce(
    (accumulatedGroups, document) => {
      return discriminatorsKeys.reduce((discriminatedGroups, discriminatorKey) => {
        if (discriminators[discriminatorKey](document)) {
          return {
            ...discriminatedGroups,
            [discriminatorKey]: [...discriminatedGroups[discriminatorKey], document],
          };
        }

        return discriminatedGroups;
      }, accumulatedGroups);
    },
    emptyGroups as Record<string, unknown[]>,
  );
};

const isVariantOfProduct = (document: JsonApiDocument, productId: string): boolean =>
  document.type === 'variant' &&
  (document.relationships['product'].data as RelationType).id === productId;

const deserializeImages = (
  product: ProductAttr,
  variants: JsonApiDocument[],
): Image[] => {
  const images = new Map<string, Image>();
  [product, ...variants].forEach((v) => {
    const publicData = v.attributes['public_metadata'] as { images?: Image[] };
    publicData.images?.forEach((image) => {
      if (!images.has(image.url)) {
        images.set(image.url, image);
      }
    });
  });

  return Array.from(images.values());
};

const partialDeserializeProductVariant = (
  product: ProductAttr,
  variant: JsonApiDocument,
  attachments: JsonApiDocument[],
): Omit<ProductVariant, 'images'> => ({
  id: variant.id,
  productId: product.id,
  variantId: variant.id,
  description: (variant.attributes['description'] ||
    product.attributes.description) as string,
  name: product.attributes.name,
  slug: product.attributes.slug,
  sku: product.attributes.sku,
  variantSku: variant.attributes['sku'] as string,
  localizedSlugs: product.attributes.localized_slugs,
  optionTypes: deserializeOptionTypes(attachments, product, variant),
  optionValues: deserializeOptionValues(attachments, variant),
  properties: deserializeProperties(attachments, product),
  categories: deserializeTaxons(attachments),
  displayPrice: variant.attributes['display_price'] as string,
  price: {
    original: convertPriceToFloat(variant.attributes['compare_at_price']),
    current: convertPriceToFloat(variant.attributes['price']),
  },
  inStock: variant.attributes['in_stock'] as boolean,
  available: product.attributes.available,
  stockQty: variant.attributes['total_on_hand'] as number,
  daysCompletionTime:
    typeof (variant.attributes['public_metadata'] as Record<string, unknown>)[
      'days_completion_time'
    ] === 'number'
      ? ((variant.attributes['public_metadata'] as Record<string, unknown>)[
          'days_completion_time'
        ] as number)
      : null,
  publicMetadata: variant.attributes['is_master']
    ? (product.attributes['public_metadata'] as Record<string, unknown>)
    : (variant.attributes['public_metadata'] as Record<string, unknown>),
});

const convertPriceToFloat = (price: unknown): number => {
  return parseFloat(typeof price === 'string' ? price : '0');
};

const deserializeOptionTypes = (
  included: JsonApiDocument[],
  product: ProductAttr,
  variant: JsonApiDocument,
): Variant['optionTypes'] => {
  const optionTypes = extractRelationships(
    included,
    'option_type',
    'option_types',
    product,
  );

  const optionValues = deserializeOptionValues(included, variant);

  return optionTypes.map((optionType) => {
    const id = parseInt(optionType.id, 10);
    const values = optionValues
      .filter((o) => o.optionTypeId === id)
      .map((ov) => ({ ...ov, variant }));

    return {
      id: parseInt(optionType.id, 10),
      type: optionType.type,
      name: optionType.attributes['name'] as string,
      position: optionType.attributes['position'] as number,
      presentation: optionType.attributes['presentation'] as string,
      values,
    };
  });
};

const deserializeOptionValues = (
  included: JsonApiDocument[],
  variant: JsonApiDocument,
): OptionValue[] => {
  const optionValues = extractRelationships(
    included,
    'option_value',
    'option_values',
    variant,
  );

  return optionValues.map((optionValue) => ({
    id: parseInt(optionValue.id, 10),
    type: optionValue.attributes['type'],
    name: optionValue.attributes['name'],
    position: optionValue.attributes['position'],
    presentation: optionValue.attributes['presentation'],
    optionTypeId: parseInt(
      (optionValue.relationships['option_type'].data as RelationType).id,
      10,
    ),
  })) as OptionValue[];
};

const deserializeProperties = (
  included: JsonApiDocument[],
  product: ProductAttr,
): Property[] => {
  const properties = extractRelationships(
    included,
    'product_property',
    'product_properties',
    product,
  );

  return properties.map((property) => ({
    description: property.attributes['description'] as string,
    name: property.attributes['name'] as string,
    value: property.attributes['value'] as string,
    filterParam: property.attributes['filter_param'] as string,
    position: property.attributes['position'] as number,
    showProperty: !!property.attributes['show_property'],
  }));
};

const deserializeTaxons = (included: JsonApiDocument[]): Category[] => {
  return (
    included
      .filter((d) => d.type === 'taxon')
      // @ts-ignore
      .map((taxon: TaxonAttr) => taxonToCategory(taxon))
  );
};
